import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { requestPasswordReset } from "../services/authApi";

/**
 * Login screen. Posts to mttr/app_api (/api/login) via AuthContext; on success
 * redirects to wherever the user was headed (or "/"). Customers/vendors log in
 * with the same userCode/email + password they use in the Smart system — MTTR
 * verifies locally.
 */
export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Forgot-password mini-flow. The confirmation text is intentionally the same
  // whether or not the account exists (no enumeration).
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotId, setForgotId] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const onForgot = async () => {
    setForgotBusy(true);
    try {
      await requestPasswordReset(forgotId.trim());
    } catch {
      /* Show the same neutral message even on transport error. */
    } finally {
      setForgotBusy(false);
      setForgotDone(true);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setError("Enter your username and password.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Login failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-100 px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL || ""}/Rebound_Logo.png`}
            alt="Rebound"
            className="h-14 w-auto mb-3"
          />
          <h1 className="text-lg font-semibold text-secondary-800">
            Rebound Control Center
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border border-secondary-200 rounded-lg shadow-sm p-6 space-y-4"
        >
          {error && (
            <div className="text-sm text-error-700 bg-error-50 border border-error-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="identifier"
              className="block text-xs font-medium text-secondary-600 mb-1"
            >
              Username or email
            </label>
            <input
              id="identifier"
              type="text"
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-xs font-medium text-secondary-600 mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <div className="pt-1 text-center">
            {!forgotOpen ? (
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-xs text-primary-700 hover:text-primary-900 hover:underline"
              >
                Forgot password?
              </button>
            ) : forgotDone ? (
              <p className="text-xs text-secondary-600">
                If that account exists, a reset link has been sent to its email.
              </p>
            ) : (
              <div className="space-y-2 text-left border-t border-secondary-100 pt-3">
                <label
                  htmlFor="forgotId"
                  className="block text-xs font-medium text-secondary-600"
                >
                  Enter your username or email
                </label>
                <input
                  id="forgotId"
                  type="text"
                  value={forgotId}
                  onChange={(e) => setForgotId(e.target.value)}
                  className="w-full min-h-[40px] px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={forgotBusy || !forgotId.trim()}
                  onClick={() => void onForgot()}
                >
                  {forgotBusy ? "Sending…" : "Send reset link"}
                </Button>
              </div>
            )}
          </div>
        </form>

        <p className="text-center text-xs text-secondary-500 mt-4">
          Use your existing Rebound SmartBox credentials.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
